import { NgClass } from '@angular/common';
import { Component, OnDestroy, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { Subscription, filter } from 'rxjs';
import { PlanType } from '@bn/app-shield/enums';
import { ShieldUpgradeModalComponent } from '@bn/app-shield/modals';
import { UserStateService } from '@bn/auth/stores';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { ClickActionEvent, StringTMap } from '@bn/shared/entities/index';
import { ContentService } from '@bn/shared/services';
import { StoreRequest } from '@bn/shared/state-store';
import { MarkAllTouchedDirective } from '@bn/web-ui';
import { AlertComponent } from '@bn/web-ui/alert/alert.component';
import { BadgeComponent } from '@bn/web-ui/badge/badge.component';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { CardComponent } from '@bn/web-ui/card/card.component';
import { ConfirmationDialogComponent } from '@bn/web-ui/dialog';
import { InputAction } from '@bn/web-ui/input/enums';
import { InputOptions } from '@bn/web-ui/input/models';
import { PremiumBannerComponent } from '@bn/web-ui/premium-banner/premium-banner.component';
import { TagSelectComponent } from '@bn/web-ui/standalone';
import { SHIELD_PATHS } from '../../constants/path.const';
import { ShieldApiStore, ShieldCustomPagesApiStore } from '../../stores';
import { AppShieldWhiteLabelPageModalComponent, WhiteLabelPageType } from './modals/app-shield-white-label-page-modal';

@Component({
  selector: 'app-shield-settings-page',
  templateUrl: './app-shield-settings.page.html',
  styleUrls: ['./app-shield-settings.page.scss'],
  imports: [
    NgClass,
    BadgeComponent,
    TagSelectComponent,
    AlertComponent,
    ButtonComponent,
    CardComponent,
    PremiumBannerComponent,
    ReactiveFormsModule,
    TooltipDirective,
    MarkAllTouchedDirective,
  ],
  standalone: true,
})
export class AppShieldSettingsPage implements OnDestroy {
  readonly #shieldStore = inject(ShieldApiStore);
  readonly #customPagesStore = inject(ShieldCustomPagesApiStore);
  readonly #dialog = inject(BsModalService);
  readonly #router = inject(Router);
  readonly contentService = inject(ContentService);
  readonly #fb = inject(NonNullableFormBuilder);

  protected readonly PlanType = PlanType;

  serStore = inject(UserStateService).userStore;
  shield = this.#shieldStore.selectedEntity();
  customPages = this.#customPagesStore.customPages;
  isLoadingToggleLogRequestHeaders = this.#shieldStore.isLoading(StoreRequest.Update, ['wafRequestHeaderLoggingEnabled']);
  isLoadingToggleLogRequestBody = this.#shieldStore.isLoading(StoreRequest.Update, ['requestBodyLoggingEnabled']);
  isLoadingWafEngineConfig = this.#shieldStore.isLoading(StoreRequest.Update, ['wafRequestIgnoredHeaders']);
  isLoadingWhiteLabelBlockResponsePages = this.#shieldStore.isLoading(StoreRequest.Update, ['whitelabelResponsePages']);
  whitelabelResponsePages = signal(false);
  headersInputOptions: InputOptions = {
    rightAddons: [
      {
        type: 'button',
        itemStyle: 'primary',
        value: 'Save',
        buttonSize: 'sm',
        action: InputAction.ButtonClick,
      },
    ],
  };
  form = this.#fb.group({
    wafRequestIgnoredHeaders: this.#fb.control<string[]>(['asdasd', 'asdasdas']),
  });

  #subscriptions = new Subscription();
  #isUpgradeModalOpen = false;

  constructor() {
    const pullZoneId = this.#shieldStore.pullZoneId();

    this.contentService.setTitle('Settings').setBackButton({
      routerLink: `/${AppRoutes.CDN}/${pullZoneId}/${AppRoutes.Shield}`,
    });

    effect(() => {
      const headers: string[] = [...(this.shield()?.wafRequestIgnoredHeaders ?? [])];
      this.form.get('wafRequestIgnoredHeaders')?.setValue(headers, { emitEvent: false });
    });

    this.form
      .get('wafRequestIgnoredHeaders')
      ?.valueChanges.pipe(takeUntilDestroyed())
      .subscribe((wafRequestIgnoredHeaders) => this.#onRequestIgnoredHeadersChange(wafRequestIgnoredHeaders));

    effect(
      () => {
        const shield = this.shield();
        this.whitelabelResponsePages.set(shield?.whitelabelResponsePages ?? false);

        if (shield?.shieldZoneId) {
          const shieldZoneId = shield.shieldZoneId;
          untracked(() => this.#customPagesStore.loadAllCustomPages({ shieldZoneId }));
        }
      },
      { allowSignalWrites: true },
    );

    toObservable(this.shield)
      .pipe(filter((shield) => !!shield))
      .pipe(takeUntilDestroyed())
      .subscribe((shield) => {
        const plan = shield?.planType || 0;

        if (plan > PlanType.Basic) {
          const planName = PlanType[plan];

          this.contentService.setBadge({
            contextType: 'premium',
            text: planName,
            tooltip: `You have Bunny Shield ${planName} enabled`,
          });
        } else {
          this.contentService.setBadge(null);
        }
      });
  }

  ngOnDestroy(): void {
    this.contentService.setBackButton(null);
    this.#subscriptions.unsubscribe();
  }

  onComparePlansClick(): void {
    const pullZoneId = this.#shieldStore.pullZoneId();
    this.#router.navigate(['/', AppRoutes.CDN, pullZoneId, SHIELD_PATHS.Shield, SHIELD_PATHS.ShieldOverview], {
      queryParams: {
        compare: 'true',
      },
    });
  }

  onOpenUpgradeModal(): void {
    const shieldId = this.#shieldStore.idSelected();

    if (!shieldId) return;

    if (!this.#isUpgradeModalOpen) this.#showUpgradeModal(+shieldId, true);
  }

  onCardInputChange(value: boolean, key: string): void {
    const shieldId = this.#shieldStore.idSelected();

    if (!shieldId) return;

    if (key === 'whitelabelResponsePages' && this.shield()?.planType === PlanType.Basic) {
      if (!this.#isUpgradeModalOpen) this.#showUpgradeModal(+shieldId, value);

      return;
    }

    this.#shieldStore.updateShield({
      id: +shieldId,
      data: { [key]: value },
      customFields: [key],
    });
  }

  onDeleteCustomPage(pageType: WhiteLabelPageType): void {
    const shieldZoneId = this.shield()?.shieldZoneId;

    if (!shieldZoneId) return;

    const confirmRef = this.#dialog.show(ConfirmationDialogComponent, {
      class: 'modal-dialog-centered modal-sm',
      initialState: {
        data: {
          contextType: 'danger',
          icon: 'file-shield',
          title: 'Revert to default?',
          description: 'This will permanently remove your custom page and restore the default bunny.net page.',
          confirmButtonName: 'Revert to default',
          event: { action: 'confirm' },
          textCenter: true,
        },
      },
    });

    confirmRef.content?.getOnAction$().subscribe((event) => {
      if (event.action === 'confirm') {
        this.#customPagesStore.resetToDefault({ shieldZoneId, pageType });
      }

      this.#dialog.hide();
    });
  }

  onEditCustomPage(pageType: WhiteLabelPageType): void {
    this.#dialog.show(AppShieldWhiteLabelPageModalComponent, {
      class: 'modal-dialog-centered modal-lg',
      ignoreBackdropClick: true,
      initialState: {
        data: {
          extra: { pageType },
        },
      },
    });
  }

  #onRequestIgnoredHeadersChange(wafRequestIgnoredHeaders: string[]): void {
    const id = this.shield()?.shieldZoneId;

    if (!id) return;

    this.#shieldStore.updateShield({
      id,
      data: { wafRequestIgnoredHeaders: wafRequestIgnoredHeaders ?? [] },
      customFields: ['wafRequestIgnoredHeaders'],
      toastr: { message: 'Excluded request headers were changed successfully.' },
    });
  }

  #showUpgradeModal(shieldZoneId: number, value: boolean): void {
    // Note: we always set it to true to be consistent to the value on GUI and later we set it back on false if it didn't went through otherwise signal don't change and
    // nothing is changed on GUI
    this.whitelabelResponsePages.set(true);

    const dialog = this.#dialog.show(ShieldUpgradeModalComponent, {
      class: 'modal-sm modal-md-xl modal-dialog-centered',
      initialState: {
        data: {
          title: 'Your brand.<br />Your experience.',
          extra: {
            requiredPlan: PlanType.Advanced,
            title: 'Unlock White-Label Block Pages',
            description: `Your current plan shows bunny.net branded block and challenge pages to your visitors. Upgrade to replace them with fully customized HTML pages that match your brand.<br /><br />With Bunny Shield Advanced, every blocked or challenged request becomes part of your seamless user experience.`,
            imageSrc: '/assets/web-ui/images/bunny-shield.svg',
            itemsList: [
              {
                icon: 'circle-check',
                text: 'Replace default block pages with your own branded HTML',
                iconContextType: 'dark',
              },
              {
                icon: 'circle-check',
                text: 'Customize challenge, block, and rate-limit pages independently',
                iconContextType: 'dark',
              },
              {
                icon: 'circle-check',
                text: 'Keep visitors on-brand even when traffic is being filtered',
                iconContextType: 'dark',
              },
            ],
          },
        },
      },
    });

    this.#subscriptions.add(
      dialog.content?.getOnAction$().subscribe((event: ClickActionEvent) => {
        if (event.action === 'confirm') {
          this.#shieldStore.updateShield({
            id: shieldZoneId,
            data: { whitelabelResponsePages: value },
            customFields: ['whitelabelResponsePages'],
          });
        } else {
          this.#dialog.hide();

          const isSuccessScreen = (event.data?.entity as StringTMap<boolean>)?.isSuccessScreen;

          if (isSuccessScreen) return;

          this.whitelabelResponsePages.set(false);
        }
      }),
    );

    this.#subscriptions.add(
      dialog.onHide?.subscribe(() => {
        this.#isUpgradeModalOpen = false;
        this.#dialog.hide();
      }),
    );

    this.#isUpgradeModalOpen = true;
  }
}
